/**
 * UnlockRequestDialog - Dialog zum Stellen eines Entsperrungsantrags
 * 
 * Ermöglicht Mitarbeitern, Entsperrung für HARD-gesperrte Einträge zu beantragen.
 */
import { useState, useEffect } from 'react';
import { Lock, Send, AlertCircle, Clock, Calendar, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { safeFormatDate } from '../lib/dateTime';
import { getErrorMessage } from '../lib/errorMessage';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const MINIMUM_REASON_LENGTH = 10;
const DEFAULT_UNLOCK_VALIDITY_HOURS = 48;

export const UnlockRequestDialog = ({
  open,
  onOpenChange,
  entries = [],
  collectionName = 'work_time',
  onSuccess,
}) => {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unlockValidityHours, setUnlockValidityHours] = useState(DEFAULT_UNLOCK_VALIDITY_HOURS);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get('/audit/settings');
        if (response.data?.unlock_validity_hours) {
          setUnlockValidityHours(response.data.unlock_validity_hours);
        }
      } catch (error) {
        // Use default
      }
    };
    if (open) {
      loadSettings();
    }
  }, [open, api]);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const isReasonValid = reason.trim().length >= MINIMUM_REASON_LENGTH;

  const handleSubmit = async () => {
    if (!isReasonValid || entries.length === 0) return;

    setIsSubmitting(true);
    try {
      const documentIds = entries.map(e => e.id);
      
      const response = await api.post('/locks/unlock-requests', {
        collection_name: collectionName,
        document_ids: documentIds,
        reason: reason.trim(),
      });

      toast.success('Entsperrungsantrag wurde gesendet', {
        description: 'Ihr Vorgesetzter wird über den Antrag informiert.',
      });

      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Fehler beim Senden des Antrags'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatEntryInfo = (entry) => {
    const date = safeFormatDate(entry.date, 'EEEE, d. MMMM yyyy', '');
    const hours = entry.hours ? `${Number(entry.hours).toFixed(2)}h` : '';
    const project = entry.project_name || entry.description || '';
    
    return { date, hours, project };
  };

  const totalHours = entries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            Entsperrung beantragen
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Die ausgewählten Einträge sind gesperrt, da die Woche bereits genehmigt wurde.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entry list */}
          <div>
            <Label className="text-muted-foreground mb-2 block">Betroffene Einträge</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {entries.map((entry, index) => {
                const info = formatEntryInfo(entry);
                return (
                  <div 
                    key={entry.id || index}
                    className="p-3 rounded-lg bg-muted/50 border border-border"
                  >
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      {info.date}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      {info.hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {info.hours}
                        </span>
                      )}
                      {info.project && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-3.5 h-3.5" />
                          {info.project}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {entries.length > 1 && (
              <p className="text-sm text-muted-foreground mt-2">
                Gesamt: {entries.length} Einträge, {totalHours.toFixed(2)} Stunden
              </p>
            )}
          </div>

          {/* Reason input */}
          <div>
            <Label className="text-muted-foreground mb-2 block">
              Begründung <span className="text-red-600 dark:text-red-400">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Warum müssen diese Einträge geändert werden? (mind. 10 Zeichen)"
              className="bg-input border-border text-foreground min-h-[100px] resize-none"
              data-testid="unlock-reason-input"
            />
            <div className="flex justify-between mt-2">
              <p className={`text-xs ${reason.length >= MINIMUM_REASON_LENGTH ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                {reason.length} / {MINIMUM_REASON_LENGTH} Zeichen (minimum)
              </p>
              {reason.length > 0 && reason.length < MINIMUM_REASON_LENGTH && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Noch {MINIMUM_REASON_LENGTH - reason.length} Zeichen erforderlich
                </p>
              )}
            </div>
          </div>

          {/* Info hint */}
          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  Nach Freigabe durch Ihren Vorgesetzten können Sie die Einträge für {unlockValidityHours} Stunden bearbeiten.
                </p>
                <p className="text-sm text-indigo-700 dark:text-indigo-300/80 mt-1">
                  Die Woche muss danach erneut genehmigt werden.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground"
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isReasonValid || isSubmitting}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="submit-unlock-request-btn"
          >
            {isSubmitting ? (
              'Wird gesendet...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Antrag senden
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnlockRequestDialog;
